// Single source of truth for the UPSC "editorial day".
//
// UPSC current affairs run on an IST calendar ("Updated 7:00 AM Daily"), and
// India observes no DST, so IST is a fixed UTC+05:30 offset. That lets us derive
// the IST calendar day deterministically on both server and client with plain
// arithmetic (no date library, no timezone-dependent Date parsing). Using this
// everywhere keeps the detail-page URL slug and the API date filter in sync.

export const UPSC_IST_TIMEZONE = 'Asia/Kolkata';

export const UPSC_IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

export interface UpscIstDateParts {
  year: number;
  month: number; // 0-based, matches Date#getMonth
  day: number;
}

const toValidDate = (input?: string | Date | null): Date | null => {
  if (!input) {
    return null;
  }

  const date = input instanceof Date ? input : new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getUpscIstDateParts = (
  input?: string | Date | null
): UpscIstDateParts | null => {
  const date = toValidDate(input);
  if (!date) {
    return null;
  }

  const istDate = new Date(date.getTime() + UPSC_IST_OFFSET_MS);
  return {
    year: istDate.getUTCFullYear(),
    month: istDate.getUTCMonth(),
    day: istDate.getUTCDate()
  };
};

// DD-MM-YYYY in IST, used for detail-page URLs.
export const getUpscIstDateSlug = (input?: string | Date | null): string => {
  const parts = getUpscIstDateParts(input);
  if (!parts) {
    return '';
  }

  const day = String(parts.day).padStart(2, '0');
  const month = String(parts.month + 1).padStart(2, '0');
  return `${day}-${month}-${parts.year}`;
};

// Start/end instants (ISO, UTC) of the IST calendar day the input falls on.
// Used to build `publishAt` range filters so listings and URLs agree.
export const getUpscIstDayRangeIso = (
  input?: string | Date | null
): { publishAt_gte?: string; publishAt_lte?: string } => {
  const parts = getUpscIstDateParts(input);
  if (!parts) {
    return {};
  }

  const dayStartUtcMs =
    Date.UTC(parts.year, parts.month, parts.day, 0, 0, 0, 0) -
    UPSC_IST_OFFSET_MS;
  const dayEndUtcMs =
    Date.UTC(parts.year, parts.month, parts.day, 23, 59, 59, 999) -
    UPSC_IST_OFFSET_MS;

  return {
    publishAt_gte: new Date(dayStartUtcMs).toISOString(),
    publishAt_lte: new Date(dayEndUtcMs).toISOString()
  };
};

export const getUpscIstMonthRangeIso = (
  year: number,
  monthIndex: number
): { publishAt_gte: string; publishAt_lte: string } => {
  const monthStartUtcMs =
    Date.UTC(year, monthIndex, 1, 0, 0, 0, 0) - UPSC_IST_OFFSET_MS;
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const monthEndUtcMs =
    Date.UTC(year, monthIndex, lastDay, 23, 59, 59, 999) - UPSC_IST_OFFSET_MS;

  return {
    publishAt_gte: new Date(monthStartUtcMs).toISOString(),
    publishAt_lte: new Date(monthEndUtcMs).toISOString()
  };
};
